import React, { useContext } from "react";
import { motion, useAnimation } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { CanvasContext } from "../../contexts/CanvasContext";
import "./ClientsProjects.css";

const CounterItem = ({ label, endValue, duration }) => {
  const { isDarkTheme } = useContext(CanvasContext);
  const [count, setCount] = React.useState(0);
  const controls = useAnimation();

  const [ref, inView] = useInView({
    triggerOnce: false,
    threshold: 0.1,
  });

  React.useEffect(() => {
    if (inView) {
      controls.start("visible");
      const end = parseInt(endValue, 10);
      const totalDuration = parseInt(duration, 10);
      if (end === 0 || totalDuration === 0) return;

      const incrementTime = totalDuration / end;
      let start = 0;

      const timer = setInterval(() => {
        start += 1;
        setCount(start);
        if (start >= end) {
          clearInterval(timer);
          setCount(end);
        }
      }, incrementTime);

      return () => clearInterval(timer);
    } else {
      controls.start("hidden");
      setCount(0);
    }
  }, [inView, endValue, duration, controls]);

  const containerVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  const contentVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, delay: 0.2 } },
  };

  return (
    <motion.div
      ref={ref}
      className={`counter-item ${isDarkTheme ? "dark-theme" : ""}`}
      variants={containerVariants}
      initial="hidden"
      animate={controls}
    >
      <motion.h2 className="counter-value" variants={contentVariants}>
        {count}
      </motion.h2>
      <motion.p className="counter-label" variants={contentVariants}>
        {label}
      </motion.p>
    </motion.div>
  );
};

const ClientsProjects = () => {
  const controls = useAnimation();
  const { isDarkTheme } = useContext(CanvasContext);

  const [ref, inView] = useInView({
    triggerOnce: false,
    threshold: 0.1,
  });

  React.useEffect(() => {
    if (inView) {
      controls.start("visible");
    } else {
      controls.start("hidden");
    }
  }, [controls, inView]);

  const containerVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, staggerChildren: 0.2 },
    },
  };

  const textVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  const reasonVariants = {
    hidden: (custom) => {
      if (custom === 1) return { opacity: 0, y: -100 };
      if (custom === 2) return { opacity: 0, y: 50 };
      if (custom === 3) return { opacity: 0, y: -100 };
    },
    visible: (custom) => {
      if (custom === 1)
        return { opacity: 1, y: 0, transition: { duration: 0.5 } };
      if (custom === 2)
        return { opacity: 1, y: 0, transition: { duration: 0.5 } };
      if (custom === 3)
        return { opacity: 1, y: 0, transition: { duration: 0.5 } };
    },
  };

  return (
    <motion.div
      ref={ref}
      className={`clients-projects ${isDarkTheme ? "dark-theme" : ""}`}
      variants={containerVariants}
      initial="hidden"
      animate={controls}
    >
      <motion.div className="stats-container" variants={containerVariants}>
        <CounterItem label="Happy Clients" endValue="10" duration="2000" />
        <CounterItem label="Hard Worker" endValue="3" duration="2000" />
        <CounterItem label="Complete Project" endValue="10" duration="2000" />
        <CounterItem label="Years Of Experience" endValue="2" duration="2000" />
      </motion.div>

      <motion.div className="why-choose-us" variants={containerVariants}>
        <motion.h3 className="section-subtitle" variants={textVariants}>
          WHY CHOOSE US
        </motion.h3>
        <motion.h2 className="section-title" variants={textVariants}>
          Why Our Clients Choose NexGen Sites
        </motion.h2>
        <motion.p className="section-description" variants={textVariants}>
          At NexGen Sites, we empower users to create stunning websites without
          writing a single line of code. Our platform is designed with
          innovation and ease-of-use in mind, ensuring that anyone can bring
          their digital vision to life effortlessly.
        </motion.p>

        <div className="reasons-container">
          <motion.div
            className="reason-item"
            variants={reasonVariants}
            custom={1}
          >
            <h3 className="reason-number">01.</h3>
            <h4 className="reason-title">Cutting-Edge Technology</h4>
            <p className="reason-description">
              We leverage the latest in web development technologies to provide
              a seamless drag-and-drop experience. From custom animations to
              advanced hover effects, our tools ensure that your website stands
              out from the competition.
            </p>
          </motion.div>
          <motion.div
            className="reason-item"
            variants={reasonVariants}
            custom={2}
          >
            <h3 className="reason-number">02.</h3>
            <h4 className="reason-title">Tailor-Made Solutions</h4>
            <p className="reason-description">
              Every website is unique, and so is our approach. NexGen Sites
              offers fully customizable components, allowing you to adjust
              colors, dimensions, and animations to match your brand's identity
              perfectly.
            </p>
          </motion.div>
          <motion.div
            className="reason-item"
            variants={reasonVariants}
            custom={3}
          >
            <h3 className="reason-number">03.</h3>
            <h4 className="reason-title">Efficient Workflows</h4>
            <p className="reason-description">
              With NexGen Sites, efficiency is at the core of everything we do.
              Our platform streamlines the website creation process with live
              previews, multi-page support, and instant code generation, helping
              you deliver projects faster and with precision.
            </p>
          </motion.div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default ClientsProjects;
